/**
 * Complementary Look – "Mach den Look komplett"
 *
 * Custom Web Component that fetches complementary products via
 * Shopify's Product Recommendations JSON API and renders:
 * 1. A compact preview card (2 images + CTA)
 * 2. A modal dialog with size pickers + combined add-to-cart
 */

if (!customElements.get('complementary-look')) {
  customElements.define(
    'complementary-look',
    class extends HTMLElement {
      constructor() {
        super();
        this.products = [];
        this.selectedVariants = {};
        this.modal = null;
      }

      connectedCallback() {
        this.shippingPolicyUrl = document.body.dataset.shippingPolicyUrl || '/policies/shipping-policy';
        this.productId = this.dataset.productId;
        if (!this.productId) return;
        this.loadProducts();
      }

      loadProducts() {
        // Read products from server-rendered JSON (Liquid metafield data)
        // No API call needed – data is always complete regardless of cart state
        const jsonEl = this.querySelector('script[data-complementary-products]');
        if (!jsonEl) {
          this.setAttribute('hidden', '');
          return;
        }

        try {
          this.products = JSON.parse(jsonEl.textContent);
        } catch (e) {
          console.error('[complementary-look] JSON parse error:', e);
          this.setAttribute('hidden', '');
          return;
        }

        if (this.products.length === 0) {
          this.setAttribute('hidden', '');
          return;
        }

        this.renderPreview();
      }

      /* ----------------------------------------------------------------
         Preview Card
         ---------------------------------------------------------------- */

      renderPreview() {
        const preview = this.querySelector('.cl-preview');
        if (!preview) return;

        preview.classList.remove('cl-preview--loading');
        preview.classList.add('cl-preview--loaded');

        const totalPrice = this.products.reduce((s, p) => s + p.price, 0);

        const imagesHTML = this.products
          .map((p) => {
            const src = this.getImageSrc(p, 300);
            return `<div class="cl-preview__img-wrap">
              ${src ? `<img src="${src}" alt="${this.escapeHTML(p.title)}" loading="lazy">` : ''}
            </div>`;
          })
          .join(this.products.length > 1 ? '<span class="cl-preview__plus">+</span>' : '');

        preview.innerHTML = `
          <h3 class="cl-preview__heading">COMPLETE THE FIT</h3>
          <div class="cl-preview__images">${imagesHTML}</div>
          <button type="button" class="cl-preview__cta" aria-haspopup="dialog">JETZT SICHERN – ${this.formatPrice(totalPrice)}</button>
        `;

        // Event delegation on the component itself for robustness
        this.addEventListener('click', (e) => {
          const cta = e.target.closest('.cl-preview__cta');
          if (cta) {
            e.preventDefault();
            e.stopPropagation();
            this.openModal();
          }
        });

        // Also make the whole preview card clickable as fallback
        preview.style.cursor = 'pointer';
        preview.addEventListener('click', (e) => {
          if (!e.target.closest('.cl-preview__cta')) {
            this.openModal();
          }
        });
      }

      /* ----------------------------------------------------------------
         Modal
         ---------------------------------------------------------------- */

      buildModal() {
        // Check if modal already exists in DOM (survives element re-creation)
        const existingModal = document.querySelector('.cl-modal');
        if (existingModal) {
          this.modal = existingModal;
          // Restore selectedVariants from DOM state (CSS classes survive re-render)
          existingModal.querySelectorAll('.cl-modal__size--selected').forEach((btn) => {
            this.selectedVariants[btn.dataset.productIndex] = btn.dataset.variantId;
          });
          // Remove old click handler (references destroyed component instance)
          if (existingModal._clickHandler) {
            existingModal.removeEventListener('click', existingModal._clickHandler);
          }
          // Re-bind events to this (new) component instance
          this.bindModalEvents();
          return;
        }
        if (this.modal) return;

        const el = document.createElement('div');
        el.className = 'cl-modal';
        el.setAttribute('role', 'dialog');
        el.setAttribute('aria-modal', 'true');
        el.setAttribute('aria-label', 'Complete the fit');

        const productsHTML = this.products
          .map((product, idx) => {
            const color = this.getProductColor(product);
            const sizes = this.getAvailableSizes(product, color);
            const imgSrc = this.getImageSrc(product, 400);
            const priceVaries = !!product.price_varies;
            const price = priceVaries
              ? `ab ${this.formatPrice(product.price_min)}`
              : this.formatPrice(product.price);

            return `
            <div class="cl-modal__product" data-product-index="${idx}">
              <div class="cl-modal__product-img">
                <a href="${product.url}" target="_blank" rel="noopener">
                  ${imgSrc ? `<img src="${imgSrc}" alt="${this.escapeHTML(product.title)}" loading="lazy" width="400" height="533">` : ''}
                </a>
              </div>
              <div class="cl-modal__product-info">
                ${product.collection ? `<span class="cl-modal__collection">${this.escapeHTML(product.collection)}</span>` : ''}
                <h3 class="cl-modal__product-name">${this.escapeHTML(product.title)}</h3>
                ${product.feature_badges && product.feature_badges.length > 0 ? `<div class="cl-modal__features">${product.feature_badges.map(fb => `<span class="cl-modal__feature">${this.escapeHTML(fb)}</span>`).join('')}</div>` : ''}
                ${product.color_label ? `<p class="cl-modal__product-color">${product.color_swatch ? `<span class="cl-modal__color-dot" style="background:${product.color_swatch}"></span>` : ''}${this.escapeHTML(product.color_label)}</p>` : (color ? `<p class="cl-modal__product-color">${this.escapeHTML(color)}</p>` : '')}
                ${!priceVaries && product.available && product.compare_at_price > product.price ? `<div class="cl-modal__product-price"><span class="cl-modal__price-sale">${price}</span><s class="cl-modal__price-compare">${this.formatPrice(product.compare_at_price)}</s><span class="cl-modal__price-save">SPARE ${this.formatPrice(product.compare_at_price - product.price)}</span></div>` : `<div class="cl-modal__product-price">${price}</div>`}
                <div class="cl-modal__tax-note caption">inkl. MwSt., zzgl. <a href="${this.shippingPolicyUrl}">Versandkosten</a></div>
                <div class="cl-modal__sizes-wrap">
                  <div class="cl-modal__sizes-label">Gr\u00f6\u00dfe:</div>
                  <div class="cl-modal__sizes">
                    ${sizes
                      .map(
                        (s) => `
                      <button type="button"
                        class="cl-modal__size${!s.available ? ' cl-modal__size--unavailable' : ''}"
                        data-variant-id="${s.variantId}"
                        data-product-index="${idx}"
                        data-price="${product.price}"
                        ${!s.available ? 'disabled' : ''}
                        aria-label="Gr\u00f6\u00dfe ${s.label}${!s.available ? ' – ausverkauft' : ''}"
                      >${s.label}</button>`
                      )
                      .join('')}
                  </div>
                </div>
              </div>
            </div>
            ${idx < this.products.length - 1 ? '<div class="cl-modal__divider"><span class="cl-modal__divider-plus">+</span></div>' : ''}`;
          })
          .join('');

        el.innerHTML = `
          <div class="cl-modal__backdrop" style="display:block;position:absolute;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.6);z-index:0"></div>
          <div class="cl-modal__dialog">
            <div class="cl-modal__header">
              <h2 class="cl-modal__heading">Complete the fit</h2>
              <p class="cl-modal__subtext">Passend zu deinem ${this.escapeHTML(this.dataset.productTitle || '')}</p>
              <button type="button" class="cl-modal__close" aria-label="Schlie\u00dfen">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
            <div class="cl-modal__content">
              ${productsHTML}
            </div>
            <div class="cl-modal__footer">
              <div class="cl-modal__summary"></div>
              <button type="button" class="cl-modal__atc" disabled>
                <span class="cl-modal__atc-text">W\u00c4HLE DEINE GR\u00d6SSEN</span>
                <span class="cl-modal__atc-spinner"></span>
              </button>
            </div>
          </div>
        `;

        document.body.appendChild(el);
        this.modal = el;
        this.bindModalEvents();
      }

      bindModalEvents() {
        // Single delegated click handler (survives component re-creation)
        const handler = (e) => {
          if (e.target.closest('.cl-modal__backdrop') || e.target.closest('.cl-modal__close')) {
            this.closeModal();
          } else if (e.target.closest('.cl-modal__size:not(.cl-modal__size--unavailable)')) {
            this.selectSize(e.target.closest('.cl-modal__size'));
          } else if (e.target.closest('.cl-modal__atc')) {
            this.addToCart();
          }
        };
        this.modal.addEventListener('click', handler);
        this.modal._clickHandler = handler;

        // Escape key
        this._escapeHandler = (e) => {
          if (e.key === 'Escape') this.closeModal();
        };
      }

      openModal() {
        this.buildModal();
        this.modal.setAttribute('open', '');
        // iOS-safe scroll lock: save position + fix body
        document.body.dataset.scrollY = window.scrollY;
        document.body.style.top = '-' + window.scrollY + 'px';
        document.body.classList.add('overflow-hidden');
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.addEventListener('keyup', this._escapeHandler);

        // Trap focus
        if (typeof trapFocus === 'function') {
          trapFocus(this.modal, this.modal.querySelector('.cl-modal__dialog'));
        }

        this._openedBy = document.activeElement;
      }

      closeModal(removeOverflowHidden = true) {
        if (!this.modal) return;
        this.modal.removeAttribute('open');
        if (removeOverflowHidden) {
          // iOS-safe scroll lock: restore position
          const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
          document.body.classList.remove('overflow-hidden');
          document.body.style.position = '';
          document.body.style.width = '';
          document.body.style.top = '';
          window.scrollTo(0, scrollY);
        }
        document.removeEventListener('keyup', this._escapeHandler);

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this._openedBy);
        }
      }

      /* ----------------------------------------------------------------
         Size Selection
         ---------------------------------------------------------------- */

      selectSize(btn) {
        const idx = btn.dataset.productIndex;
        const container = btn.closest('.cl-modal__product');
        const wasSelected = btn.classList.contains('cl-modal__size--selected');

        // Deselect siblings
        container.querySelectorAll('.cl-modal__size').forEach((s) => {
          s.classList.remove('cl-modal__size--selected');
        });

        if (wasSelected) {
          // Toggle off – deselect this size
          delete this.selectedVariants[idx];
        } else {
          // Select this size
          btn.classList.add('cl-modal__size--selected');
          this.selectedVariants[idx] = btn.dataset.variantId;
        }

        this.updateATCState();
      }

      updateATCState() {
        const atcBtn = this.modal.querySelector('.cl-modal__atc');
        const atcText = atcBtn.querySelector('.cl-modal__atc-text');
        const summary = this.modal.querySelector('.cl-modal__summary');

        // Calculate selected items count, total price, savings and compare total
        let count = 0;
        let total = 0;
        let totalSavings = 0;
        let totalCompare = 0;
        this.products.forEach((product, idx) => {
          if (this.selectedVariants[idx]) {
            count++;
            total += product.price;
            if (product.available && product.compare_at_price > product.price) {
              totalSavings += (product.compare_at_price - product.price);
              totalCompare += product.compare_at_price;
            } else {
              totalCompare += product.price;
            }
          }
        });

        const anySelected = count > 0;
        atcBtn.disabled = !anySelected;

        if (anySelected) {
          const totalFormatted = this.formatPrice(total);
          if (totalSavings > 0) {
            atcText.innerHTML = `<span class="cl-modal__atc-label">IN DEN WARENKORB</span><span class="cl-modal__atc-prices"><s class="cl-modal__atc-compare">${this.formatPrice(totalCompare)}</s><span><span class="cl-modal__atc-current">${totalFormatted}</span><span class="cl-modal__atc-save">SPARE ${this.formatPrice(totalSavings)}</span></span></span>`;
          } else {
            atcText.innerHTML = `<span class="cl-modal__atc-label">IN DEN WARENKORB</span><span class="cl-modal__atc-price-single">${totalFormatted}</span>`;
          }
          summary.textContent = `${count} Artikel \u00b7 ${totalFormatted}`;
          summary.style.display = '';
        } else {
          atcText.textContent = 'W\u00c4HLE DEINE GR\u00d6SSEN';
          summary.style.display = 'none';
        }
      }

      /* ----------------------------------------------------------------
         Add to Cart
         ---------------------------------------------------------------- */

      async addToCart() {
        const atcBtn = this.modal.querySelector('.cl-modal__atc');
        if (atcBtn.disabled) return;

        atcBtn.classList.add('cl-modal__atc--loading');
        atcBtn.disabled = true;

        // Only include products where a size was actually selected
        const items = this.products
          .map((_, idx) => this.selectedVariants[idx])
          .filter(Boolean)
          .map((variantId) => ({
            id: parseInt(variantId, 10),
            quantity: 1,
          }));

        if (items.length === 0) {
          atcBtn.classList.remove('cl-modal__atc--loading');
          atcBtn.disabled = false;
          return;
        }

        try {
          const cartDrawer = document.querySelector('cart-drawer');
          const sectionIds = cartDrawer && cartDrawer.getSectionsToRender
            ? cartDrawer.getSectionsToRender().map((s) => s.id)
            : [];

          const body = { items };
          if (sectionIds.length > 0) {
            // Shopify expects sections as comma-separated string, not array
            body.sections = sectionIds.join(',');
            body.sections_url = window.location.pathname;
          }

          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });

          if (!res.ok) throw new Error(res.statusText);

          const result = await res.json();

          // Publish cart update event (same pattern as Dawn's product-form.js)
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'complementary-look',
              cartData: result,
            });
          }

          // Reset selections so the section stays usable for another add
          this.resetSelections();
          // Don't remove overflow-hidden if cart drawer will open right after
          this.closeModal(!cartDrawer);

          if (cartDrawer) {
            // Remove is-empty class so .cart__contents becomes visible
            // (CSS: .is-empty .cart__contents { display: none })
            if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');

            if (result.sections) {
              // Pass full response like Dawn does (renderContents expects parsedState)
              cartDrawer.renderContents(result);
            } else {
              // Fallback: fetch sections separately
              this.fetchAndOpenDrawer(cartDrawer);
            }
          } else {
            window.location.href = '/cart';
          }
        } catch (e) {
          console.error('[complementary-look] add to cart error:', e);
        } finally {
          atcBtn.classList.remove('cl-modal__atc--loading');
          atcBtn.disabled = false;
        }
      }

      async fetchAndOpenDrawer(cartDrawer) {
        try {
          const sections = cartDrawer.getSectionsToRender().map((s) => s.id);
          const res = await fetch(
            `${window.location.pathname}?sections=${sections.join(',')}`
          );
          if (!res.ok) throw new Error(res.statusText);
          const data = await res.json();
          cartDrawer.renderContents({ sections: data });
        } catch (e) {
          console.error('[complementary-look] cart drawer fallback error:', e);
          window.location.href = '/cart';
        }
      }

      resetSelections() {
        this.selectedVariants = {};
        if (this.modal) {
          this.modal.querySelectorAll('.cl-modal__size--selected').forEach((s) => {
            s.classList.remove('cl-modal__size--selected');
          });
          this.updateATCState();
        }
      }

      /* ----------------------------------------------------------------
         Helpers
         ---------------------------------------------------------------- */

      // Shopify JSON API returns options as objects {name: "Size"} or strings
      getOptionName(o) {
        if (typeof o === 'string') return o.toLowerCase();
        if (o && o.name) return o.name.toLowerCase();
        return '';
      }

      getProductColor(product) {
        if (!product.variants || product.variants.length === 0) return '';
        const first = product.variants.find((v) => v.available) || product.variants[0];
        if (product.options) {
          const colorIdx = product.options.findIndex(
            (o) => {
              const name = this.getOptionName(o);
              return name.includes('color') || name.includes('farbe');
            }
          );
          if (colorIdx >= 0) {
            return first[`option${colorIdx + 1}`] || '';
          }
        }
        return '';
      }

      getAvailableSizes(product, color) {
        if (!product.variants) return [];

        let sizeIdx = -1;
        let colorIdx = -1;
        if (product.options) {
          sizeIdx = product.options.findIndex(
            (o) => {
              const name = this.getOptionName(o);
              return name.includes('size') || name.includes('größe') || name.includes('groesse');
            }
          );
          colorIdx = product.options.findIndex(
            (o) => {
              const name = this.getOptionName(o);
              return name.includes('color') || name.includes('farbe');
            }
          );
        }

        if (sizeIdx < 0) {
          // No size option – return all variants as selectable
          return product.variants.map((v) => ({
            label: v.title,
            variantId: v.id,
            available: v.available,
          }));
        }

        const sizeKey = `option${sizeIdx + 1}`;
        const colorKey = colorIdx >= 0 ? `option${colorIdx + 1}` : null;

        // Filter by color if available
        const filtered = colorKey && color
          ? product.variants.filter((v) => v[colorKey] === color)
          : product.variants;

        // Deduplicate by size
        const seen = new Set();
        return filtered
          .filter((v) => {
            const size = v[sizeKey];
            if (seen.has(size)) return false;
            seen.add(size);
            return true;
          })
          .map((v) => ({
            label: v[sizeKey],
            variantId: v.id,
            available: v.available,
          }));
      }

      getImageSrc(product, width) {
        // Try multiple image sources from Shopify JSON API
        let src = '';
        if (product.images && product.images.length > 0) {
          src = product.images[0].src || product.images[0];
        } else if (product.featured_image) {
          src = typeof product.featured_image === 'string'
            ? product.featured_image
            : product.featured_image.src || '';
        } else if (product.image && product.image.src) {
          src = product.image.src;
        }
        if (!src) return '';
        return this.resizeImage(src, width);
      }

      formatPrice(price) {
        // Shopify JSON API returns price in cents (e.g. 3490 = 34,90€)
        const num = parseFloat(price);
        if (isNaN(num)) return '';
        return (num / 100).toFixed(2).replace('.', ',') + '\u20AC';
      }

      resizeImage(src, width) {
        if (!src) return '';
        // Handle protocol-relative URLs
        const fullSrc = src.startsWith('//') ? 'https:' + src : src;
        try {
          const url = new URL(fullSrc);
          url.searchParams.set('width', width);
          return url.toString();
        } catch {
          return fullSrc;
        }
      }

      escapeHTML(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      }
    }
  );
}
